import React, { useState } from "react";
import Navigation from "../../shared/Navigation";
import { useLocation } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Helmet } from 'react-helmet';

const formatINR = (value) =>
  value.toLocaleString("en-IN", { maximumFractionDigits: 0 });

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ReverseSIPCalculator = () => {
  const [targetAmount, setTargetAmount] = useState(1000000);
  const [years, setYears] = useState(10);
  const [riskProfile, setRiskProfile] = useState(12); // default Moderate
  const [monthlyInvestment, setMonthlyInvestment] = useState(null);
  const [error, setError] = useState("");
  const location = useLocation();
  const [goalType, setGoalType] = useState("Custom");

  const riskProfiles = [
    { label: "Conservative (10%)", value: 10 },
    { label: "Moderate (12%)", value: 12 },
    { label: "Aggressive (15%)", value: 15 },
  ];

  const goalTypes = [
    { label: "Retirement", defaultTarget: 10000000, defaultYears: 25 },
    { label: "Child Education", defaultTarget: 2000000, defaultYears: 15 },
    { label: "House Purchase", defaultTarget: 5000000, defaultYears: 10 },
    { label: "Wealth Creation", defaultTarget: 1000000, defaultYears: 10 },
    { label: "Custom", defaultTarget: 1000000, defaultYears: 10 },
  ];

  // On mount, check for query params and set values if present
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qTarget = params.get("targetamount");
    const qYears = params.get("durationInYears");
    const qRate = params.get("roi");
    const qGoal = params.get("goalType");
    let updated = false;
    if (qGoal) {
      const foundGoal = goalTypes.find(
        (g) => g.label.toLowerCase() === qGoal.toLowerCase()
      );
      if (foundGoal) {
        setGoalType(foundGoal.label);
        if (foundGoal.label !== "Custom") {
          setTargetAmount(foundGoal.defaultTarget);
          setYears(foundGoal.defaultYears);
        }
        updated = true;
      }
    }
    if (qTarget && !isNaN(Number(qTarget))) {
      setTargetAmount(Number(qTarget));
      updated = true;
    }
    if (qYears && !isNaN(Number(qYears))) {
      setYears(Number(qYears));
      updated = true;
    }
    if (qRate && !isNaN(Number(qRate))) {
      setRiskProfile(Number(qRate));
      updated = true;
    }
    // eslint-disable-next-line
  }, []);

  // Auto-calculate on input change
  React.useEffect(() => {
    setError("");
    const months = years * 12;
    const monthlyRate = riskProfile / (12 * 100);
    if (monthlyRate === 0 || months === 0) {
      setError("Please enter a valid duration and rate of return.");
      setMonthlyInvestment(null);
      return;
    }
    // Reverse SIP formula
    const sip =
      (targetAmount * monthlyRate) /
      ((Math.pow(1 + monthlyRate, months) - 1) * (1 + monthlyRate));
    if (isNaN(sip) || !isFinite(sip)) {
      setError("Invalid input values.");
      setMonthlyInvestment(null);
      return;
    }
    setMonthlyInvestment(Math.ceil(sip));
  }, [targetAmount, years, riskProfile]);

  // Compute chart data for investment growth
  const chartData = React.useMemo(() => {
    if (monthlyInvestment === null || error) return null;
    const monthlyRate = riskProfile / (12 * 100);
    const data = Array.from({ length: years + 1 }, (_, i) => {
      const monthsTillNow = i * 12;
      const investedSoFar = monthlyInvestment * monthsTillNow;
      const currentValue =
        monthlyInvestment *
        ((Math.pow(1 + monthlyRate, monthsTillNow) - 1) / monthlyRate) *
        (1 + monthlyRate);
      return {
        year: i,
        invested: investedSoFar,
        value: Math.round(currentValue),
      };
    });
    return {
      labels: data.map((d) => `Year ${d.year}`),
      datasets: [
        {
          label: "Portfolio Value",
          data: data.map((d) => d.value),
          borderColor: "#6366f1",
          backgroundColor: "rgba(99, 102, 241, 0.1)",
          fill: true,
          tension: 0.3,
        },
        {
          label: "Invested Amount",
          data: data.map((d) => d.invested),
          borderColor: "#a5b4fc",
          backgroundColor: "rgba(165, 180, 252, 0.1)",
          fill: false,
          borderDash: [6, 6],
          tension: 0.3,
        },
      ],
    };
  }, [monthlyInvestment, years, riskProfile, error]);

  // Helper for validation
  const getValidationWarning = (targetAmount, years, riskProfile) => {
    if (targetAmount < 10000) return "Target amount is too low.";
    if (targetAmount > 100000000) return "Target amount is unusually high.";
    if (years < 1) return "Time period should be at least 1 year.";
    if (years > 50) return "Time period is too long.";
    if (riskProfile < 8) return "Selected risk/return is very low.";
    if (riskProfile > 20) return "Selected risk/return is very high.";
    // Heuristic: Warn if SIP is more than 20% of target or less than 0.1%
    if (
      monthlyInvestment &&
      monthlyInvestment * 12 * years > targetAmount * 1.2
    )
      return "Your SIP and duration may exceed your target. Consider lowering SIP or increasing target.";
    if (
      monthlyInvestment &&
      monthlyInvestment * 12 * years < targetAmount * 0.1
    )
      return "Your SIP may be too low for this goal.";
    return "";
  };

  // --- Mutual Fund Recommendations Data ---
  const fundRecommendations = {
    10: [
      {
        name: "HDFC Hybrid Debt Fund",
        type: "Conservative",
        desc: "A balanced fund with focus on debt and limited equity exposure.",
        url: "https://www.hdfcfund.com/mutual-funds/hybrid-funds/hdfc-hybrid-debt-fund",
      },
      {
        name: "ICICI Prudential Regular Savings Fund",
        type: "Conservative",
        desc: "Aimed at steady returns with low risk.",
        url: "https://www.icicipruamc.com/mutual-fund-schemes/hybrid/icici-prudential-regular-savings-fund",
      },
    ],
    12: [
      {
        name: "Mirae Asset Large Cap Fund",
        type: "Moderate",
        desc: "Diversified equity fund with a large-cap focus.",
        url: "https://www.miraeassetmf.co.in/mutual-fund/mirae-asset-large-cap-fund",
      },
      {
        name: "Axis Bluechip Fund",
        type: "Moderate",
        desc: "Bluechip equity fund for moderate risk investors.",
        url: "https://www.axismf.com/mutual-funds/axis-bluechip-fund",
      },
    ],
    15: [
      {
        name: "Nippon India Small Cap Fund",
        type: "Aggressive",
        desc: "High growth potential with higher risk.",
        url: "https://mf.nipponindiaim.com/FundsAndPerformance/ProductListing/Pages/NipponIndiaSmallCapFund.aspx",
      },
      {
        name: "SBI Magnum Midcap Fund",
        type: "Aggressive",
        desc: "Midcap equity fund for aggressive investors.",
        url: "https://www.sbimf.com/en-us/product/magnum-midcap-fund/regular-plan/growth",
      },
    ],
  };

  // --- Lead Capture Modal ---
  function LeadCaptureModal({ open, onClose, onSubmit }) {
    const [name, setName] = React.useState("");
    const [contact, setContact] = React.useState("");
    const [submitted, setSubmitted] = React.useState(false);
    const [error, setError] = React.useState("");

    const validate = () => {
      if (!name.trim()) return "Please enter your name.";
      if (!contact.trim()) return "Please enter your phone or email.";
      if (
        !/^[\w\-.]+@[\w\-.]+\.[a-zA-Z]{2,}$/.test(contact) &&
        !/^\d{10}$/.test(contact)
      )
        return "Enter a valid email or 10-digit phone number.";
      return "";
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      const err = validate();
      if (err) {
        setError(err);
        return;
      }
      setSubmitted(true);
      setError("");
      if (onSubmit) onSubmit({ name, contact });
    };

    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm relative">
          <button
            className="absolute top-2 right-3 text-gray-400 hover:text-gray-700 text-xl"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              <h2 className="text-lg font-bold mb-2 text-center">
                Request Expert Callback
              </h2>
              <div className="mb-3">
                <label className="block text-xs font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="block text-xs font-medium mb-1">
                  Phone or Email
                </label>
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                  required
                />
              </div>
              {error && (
                <div className="text-red-500 text-xs mb-2">{error}</div>
              )}
              <button
                type="submit"
                className="w-full bg-indigo-600 text-white py-2 rounded-md font-semibold mt-2 hover:bg-indigo-700"
              >
                Request Callback
              </button>
            </form>
          ) : (
            <div className="text-center py-6">
              <div className="text-green-600 text-2xl mb-2">✓</div>
              <div className="font-semibold mb-1">Thank you!</div>
              <div className="text-sm text-gray-600">
                Our expert will contact you soon.
              </div>
              <button
                className="mt-4 px-4 py-1 bg-indigo-500 text-white rounded-md"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const [showLeadModal, setShowLeadModal] = React.useState(false);
  const [leadSubmitted, setLeadSubmitted] = React.useState(false);

  const handleLeadSubmit = (data) => {
    setLeadSubmitted(true);
    setShowLeadModal(false);
    // Here you can integrate with backend/analytics if needed
  };

  return (
    <>
      <Helmet>
        <title>Target SIP Calculator - Calculate Required SIP for Your Goal | BuildMyWealth</title>
        <meta name="description" content="Find out the monthly SIP needed to reach your financial goal. Use the Target SIP Calculator by BuildMyWealth for goal-based investment planning." />
        <meta name="keywords" content="Target SIP Calculator, Reverse SIP, SIP Goal Calculator, Mutual Fund SIP, Investment Planning, BuildMyWealth" />
        <link rel="canonical" href="https://www.buildmywealth.net/calculators/reverse-sip" />
      </Helmet>
      <Navigation />
      <div className="w-full max-w-3xl mx-auto py-6 pt-28 px-2 sm:px-4">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Target SIP Calculator
        </h1>
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                Goal Type
                <span title="Choose a goal to prefill typical target and duration. Custom lets you enter your own.">
                  <AiOutlineInfoCircle className="inline text-blue-400" />
                </span>
              </label>
              <select
                value={goalType}
                onChange={(e) => {
                  const selected = goalTypes.find(
                    (g) => g.label === e.target.value
                  );
                  setGoalType(selected.label);
                  if (selected.label !== "Custom") {
                    setTargetAmount(selected.defaultTarget);
                    setYears(selected.defaultYears);
                  }
                }}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm mb-2"
              >
                {goalTypes.map((g) => (
                  <option key={g.label} value={g.label}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                Target Amount (₹)
                <span title="The amount you want to accumulate. Should be realistic for your goal.">
                  <AiOutlineInfoCircle className="inline text-blue-400" />
                </span>
              </label>
              <input
                type="number"
                min="10000"
                value={targetAmount}
                onChange={(e) => setTargetAmount(Number(e.target.value))}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                Time Period (Years)
                <span title="How many years you have to reach your goal.">
                  <AiOutlineInfoCircle className="inline text-blue-400" />
                </span>
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                Risk Profile
                <span title="Conservative: 10% (safer, lower returns). Moderate: 12%. Aggressive: 15% (higher risk, higher returns).">
                  <AiOutlineInfoCircle className="inline text-blue-400" />
                </span>
              </label>
              <select
                value={riskProfile}
                onChange={(e) => setRiskProfile(Number(e.target.value))}
                className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 text-sm"
              >
                {riskProfiles.map((profile) => (
                  <option key={profile.value} value={profile.value}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
          {monthlyInvestment !== null && !error && (
            <div className="mt-4 bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg text-center">
              <p className="text-lg font-semibold text-gray-700 mb-1">
                Required Monthly SIP
              </p>
              <p className="text-3xl font-bold text-indigo-600">
                ₹{formatINR(monthlyInvestment)}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                To reach your target of ₹{formatINR(targetAmount)} in {years}{" "}
                years at {riskProfile}% annual return.
              </p>
            </div>
          )}

          {/* Enhancement: Investment Summary Chart */}
          {chartData && (
            <div className="mt-8 bg-white rounded-xl shadow p-4">
              <Line
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                      labels: { font: { size: 13 } },
                    },
                    title: {
                      display: true,
                      text: "Investment Growth Projection",
                      font: { size: 16 },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `₹${formatINR(value)}`,
                        font: { size: 12 },
                      },
                    },
                    x: {
                      ticks: { font: { size: 12 } },
                    },
                  },
                }}
                height={260}
              />
            </div>
          )}

          {/* Show validation warning if present */}
          {(() => {
            const warn = getValidationWarning(targetAmount, years, riskProfile);
            return warn ? (
              <div className="text-yellow-600 text-xs mb-2 flex items-center gap-1">
                <AiOutlineInfoCircle className="inline" /> {warn}
              </div>
            ) : null;
          })()}

          {/* --- Mutual Fund Recommendations --- */}
          {monthlyInvestment !== null && !error && (
            <div className="mt-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-indigo-700">
                  Suitable Mutual Fund Category
                </span>
                <AiOutlineInfoCircle
                  className="text-blue-400"
                  title="Based on your selected risk profile."
                />
              </div>
              <div className="text-sm text-gray-700 mb-2">
                {riskProfile === 10 && (
                  <>
                    <span className="font-semibold text-green-700">Conservative:</span> Consider investing in <span className="font-semibold">Hybrid Debt-Oriented Funds</span> or <span className="font-semibold">Short Duration Debt Funds</span> for stable returns with lower risk.
                  </>
                )}
                {riskProfile === 12 && (
                  <>
                    <span className="font-semibold text-yellow-700">Moderate:</span> <span className="font-semibold">Large Cap Equity Funds</span> or <span className="font-semibold">Balanced Hybrid Funds</span> are suitable for moderate risk and growth potential.
                  </>
                )}
                {riskProfile === 15 && (
                  <>
                    <span className="font-semibold text-red-700">Aggressive:</span> <span className="font-semibold">Mid Cap</span> or <span className="font-semibold">Small Cap Equity Funds</span> can be considered for higher growth and higher risk appetite.
                  </>
                )}
              </div>
              <div className="mt-4 text-center">
                <a
                  href="https://wa.me/919616622620?text=Hi%2C%20I%20need%20help%20with%20SIP%20planning%20on%20BuildMyWealth.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-4 py-2 rounded-md font-semibold hover:bg-green-600 inline-block"
                  style={{ textDecoration: 'none' }}
                >
                  <span role="img" aria-label="WhatsApp" className="mr-2">💬</span>
                  Get Expert Help on WhatsApp
                </a>
              </div>
            </div>
          )}
          {/* Enhancement: Yearly projection table - moved below SIP result */}
          {monthlyInvestment !== null && !error && (
            <div className="overflow-x-auto mt-6 mb-4">
              <table className="min-w-full text-xs text-gray-700 border rounded-lg">
                <thead>
                  <tr className="bg-indigo-50">
                    <th className="px-2 py-1 font-semibold">Year</th>
                    <th className="px-2 py-1 font-semibold">
                      Total Invested (₹)
                    </th>
                    <th className="px-2 py-1 font-semibold">
                      Projected Value (₹)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: years + 1 }, (_, i) => {
                    const monthsTillNow = i * 12;
                    const investedSoFar = monthlyInvestment * monthsTillNow;
                    const monthlyRate = riskProfile / (12 * 100);
                    const currentValue =
                      monthlyInvestment *
                      ((Math.pow(1 + monthlyRate, monthsTillNow) - 1) /
                        monthlyRate) *
                      (1 + monthlyRate);
                    return (
                      <tr
                        key={i}
                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-2 py-1 text-center">{i}</td>
                        <td className="px-2 py-1 text-right">
                          ₹{formatINR(investedSoFar)}
                        </td>
                        <td className="px-2 py-1 text-right">
                          ₹{formatINR(Math.round(currentValue))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {/* --- Lead Capture Modal --- */}
          <LeadCaptureModal
            open={showLeadModal}
            onClose={() => setShowLeadModal(false)}
            onSubmit={(data) => {
              // You can add API call here to save the lead
              setTimeout(() => setShowLeadModal(false), 2000);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default ReverseSIPCalculator;
